import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import { testimonials } from '../data/products';
import { blogPosts } from '../data/blogPosts';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../hooks/useProducts';

/* ── tiny fade-in hook ── */
function useFadeIn() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function FadeSection({ children, className = '' }) {
  const [ref, visible] = useFadeIn();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
    >
      {children}
    </div>
  );
}

/* ── Concerns ── */
const concerns = [
  { label: 'Dry Skin', image: 'dryskin' },
  { label: 'Oily Skin', image: 'oilyskin' },
  { label: 'Acne & Scars', image: 'acnescars' },
  { label: 'Dark Spots', image: 'darkspots' },
  { label: 'Fine Lines', image: 'finelines' },
];

function ConcernImage({ label, image }) {
  const sources = [
    `/${image}.jpeg`,
    `/${image}.jpg`,
    `/${image}.png`,
    `/${image}.avif`,
    `/${image}.webp`,
  ];
  const [srcIndex, setSrcIndex] = useState(0);

  return (
    <img
      src={sources[srcIndex]}
      alt={label}
      onError={() => {
        if (srcIndex < sources.length - 1) {
          setSrcIndex(srcIndex + 1);
        }
      }}
      className="h-40 w-full object-cover sm:h-48 lg:h-56"
      loading="lazy"
    />
  );
}

function WatchVideoCard({ product, videoName }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [sourceIndex, setSourceIndex] = useState(0);
  const sources = [
    `/${videoName}.mp4`,
    `/${videoName}.webm`,
    `/${videoName}.ogg`,
    `/${videoName}.mov`,
  ];

  const playVideo = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => { });
    }
  };

  const pauseVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const toggleMute = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!videoRef.current) return;

    const nextMuted = !videoRef.current.muted;
    videoRef.current.muted = nextMuted;
    setMuted(nextMuted);
  };

  return (
    <div
      className="group cursor-pointer"
      onMouseEnter={playVideo}
      onMouseLeave={pauseVideo}
    >
      <div className="relative aspect-[9/16] overflow-hidden rounded-2xl bg-[#fdf8f6]">
        <video
          ref={videoRef}
          muted={muted}
          loop
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
          onError={() => {
            if (sourceIndex < sources.length - 1) {
              setSourceIndex(sourceIndex + 1);
            }
          }}
        >
          <source src={sources[sourceIndex]} />
        </video>

        <button
          type="button"
          aria-label={muted ? 'Unmute video' : 'Mute video'}
          onClick={toggleMute}
          className="absolute right-3 top-3 rounded-full bg-black/65 p-2 text-white transition-colors hover:bg-black"
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <p className="truncate pr-2 text-xs text-gray-500">{product.shortName}</p>
        <Link
          to={`/product/${product.id}`}
          onClick={e => e.stopPropagation()}
          className="flex-shrink-0 text-[10px] border border-black px-2.5 py-1 tracking-widest uppercase hover:bg-black hover:text-white transition-colors"
        >
          Buy
        </Link>
      </div>
    </div>
  );
}

export default function Home() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeBanner, setActiveBanner] = useState(0);
  const { products, loading } = useProducts();
  const featured = products.filter(p => p.badge).slice(0, 3);
  const banners = [
    '/banner1.png',
    '/banner2.png',
    '/banner3.png',
    '/banner4.png',
    '/banner5.png',
  ];

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveBanner(current => (current + 1) % banners.length);
    }, 10000);

    return () => window.clearInterval(interval);
  }, [banners.length]);

  return (
    <main>
      {/* ── BANNER SLIDER ── */}
      <section className="relative overflow-hidden bg-[#fdf8f6]">
        <div className="relative w-full">
          <div className="relative h-[280px] w-full overflow-hidden bg-gray-100 sm:h-[360px] lg:h-[460px]">
            {banners.map((banner, index) => (
              <img
                key={banner}
                src={banner}
                alt={`Metameni banner ${index + 1}`}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${index === activeBanner ? 'opacity-100' : 'opacity-0'
                  }`}
              />
            ))}

            <div className="absolute inset-x-0 bottom-4 flex justify-center gap-2">
              {banners.map((banner, index) => (
                <button
                  key={banner}
                  type="button"
                  aria-label={`Show banner ${index + 1}`}
                  onClick={() => setActiveBanner(index)}
                  className={`h-2.5 rounded-full transition-all ${index === activeBanner ? 'w-8 bg-black' : 'w-2.5 bg-black/60'
                    }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CONCERNS ── */}
      <FadeSection>
        <section className="pt-20 pb-10 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-3 text-center">Personalise</p>
          <h2 className="font-display text-4xl sm:text-5xl font-light text-center mb-12">
            Find the right formula for your skin
          </h2>
          <div className="mx-auto grid max-w-6xl grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
            {concerns.map(c => (
              <Link
                key={c.label}
                to={`/shop?concern=${encodeURIComponent(c.label)}`}
                className="group overflow-hidden rounded-2xl border border-gray-200 transition-all duration-300 hover:border-black"
              >
                <div className="overflow-hidden bg-[#fdf8f6]">
                  <ConcernImage label={c.label} image={c.image} />
                </div>
                <div className="px-4 py-5 text-center">
                  <span className="text-sm tracking-wide font-medium leading-tight text-black">
                    {c.label}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </FadeSection>

      {/* ── FEATURED PRODUCTS ── */}
      <FadeSection>
        <section className="py-20 bg-[#fdf8f6]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2">Curated</p>
                <h2 className="font-display text-4xl sm:text-5xl font-light">Our products</h2>
              </div>
              <Link
                to="/shop"
                className="hidden sm:flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase border-b border-black pb-0.5 hover:text-gray-500 transition-colors"
              >
                View All <ChevronRight size={12} />
              </Link>
            </div>
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-400 text-sm font-light">Loading products...</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-6 lg:gap-8">
                {featured.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
            <div className="text-center mt-10 sm:hidden">
              <Link to="/shop" className="text-xs tracking-widest uppercase border-b border-black pb-0.5">
                View All Products
              </Link>
            </div>
          </div>
        </section>
      </FadeSection>

      {/* ── WATCH & SHOP (video section placeholder) ── */}
      {/* <FadeSection>
        <section className="py-20 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2">Watch &amp; Shop</p>
          <h2 className="font-display text-4xl sm:text-5xl font-light mb-10">See it in action</h2>
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-sm font-light">Loading products...</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
              {products.slice(0, 5).map((product, index) => (
                <WatchVideoCard
                  key={product.id}
                  product={product}
                  videoName={`watch${index + 1}`}
                />
              ))}
            </div>
          )}
        </section>
      </FadeSection> */}

      {/* ── BRAND STORY SNIPPET ── */}
      {/* <FadeSection>
        <section className="py-24 bg-black text-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-xs tracking-[0.35em] uppercase text-[#cc7c66] mb-6">Our Philosophy</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mb-8">
              Skincare should be<br />
              <em className="not-italic text-[#f5e6e0]">honest</em>, not complex.
            </h2>
            <p className="text-gray-400 text-base leading-relaxed max-w-2xl mx-auto font-light">
              We started Metameni with one belief — effective skincare doesn't need to be expensive,
              mysterious or full of claims. Just the right actives, at the right concentrations,
              formulated for Indian skin.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-10 border border-white/40 text-white px-8 py-4 text-xs tracking-[0.25em] uppercase font-medium hover:bg-white hover:text-black transition-colors"
            >
              Read Our Story <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      </FadeSection> */}

      {/* ── TESTIMONIALS ── */}
      <FadeSection>
        <section className="pt-12 pb-24 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2 text-center">Reviews</p>
          <h2 className="font-display text-4xl sm:text-5xl font-light text-center mb-14">What they say</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div
                key={t.id}
                className="p-8 border border-gray-100 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-center gap-0.5 mb-5">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} size={12} className="fill-black text-black" />
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed font-light italic font-display text-lg">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3 mt-6">
                  <div className="w-9 h-9 rounded-full bg-[#f5e6e0] flex items-center justify-center text-xs font-medium text-[#cc7c66]">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{t.name}</p>
                    <p className="text-xs text-gray-400">{t.location} · {t.product}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </FadeSection>

      {/* ── BLOG ── */}
      <FadeSection>
        <section className="pb-24 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2 text-center">Blog</p>
          <h2 className="font-display text-4xl sm:text-5xl font-light text-center mb-12">
            Learn the science behind better skin
          </h2>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {blogPosts.map(post => (
              <article
                key={post.id}
                className="rounded-2xl border border-gray-200 bg-white p-6 transition-colors hover:border-black"
              >
                <div className="mb-4 flex items-center gap-2">
                  <span className="rounded-full bg-[#fdf1ec] px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#cc7c66]">
                    {post.category}
                  </span>
                  <span className="text-[11px] text-gray-400">{post.date}</span>
                </div>

                <h3 className="font-display text-2xl font-light leading-tight text-black mb-3">
                  {post.title}
                </h3>

                <p className="text-sm leading-relaxed text-gray-600 font-light mb-5">
                  {post.snippet}
                </p>

                <Link
                  to="/blog"
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] border-b border-black pb-0.5 hover:text-gray-500 transition-colors"
                >
                  Read More <ArrowRight size={12} />
                </Link>
              </article>
            ))}
          </div>
        </section>
      </FadeSection>

      {/* ── TRUST BADGES ── */}
      {/* <FadeSection>
        <section className="py-14 border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
              {[
                { label: 'Dermatologically Tested', icon: '🔬' },
                { label: 'No Harmful Fragrances', icon: '🌿' },
                { label: 'Cruelty Free', icon: '🐰' },
                { label: 'Made in India', icon: '🇮🇳' },
              ].map(b => (
                <div key={b.label} className="flex flex-col items-center gap-2">
                  <span className="text-3xl">{b.icon}</span>
                  <p className="text-xs tracking-wide text-gray-500 font-medium">{b.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </FadeSection> */}

      {/* ── METAMENI PROMISE STRIP ── */}
      <section className="promise-strip">
        <div className="promise-track">

          <div className="promise-item">
            <p>The Metameni Promise</p>

            <h3>No Secrets. Only Science.</h3>

            <span>
              Purposeful ingredients. Transparent formulations. Advanced skincare technology designed for modern skin.
            </span>
          </div>

          <div className="promise-item">
            <p>The Metameni Promise</p>

            <h3>No Secrets. Only Science.</h3>

            <span>
              Purposeful ingredients. Transparent formulations. Advanced skincare technology designed for modern skin.
            </span>
          </div>

        </div>
      </section>

    </main>
  );
}
